import React, { useEffect, useRef, useState } from 'react';
import '../pages/GalleryWeekend.css';

const STRAPI_URL = process.env.REACT_APP_STRAPI_API_URL;

function getNowImageUrl(entry) {
  const formats = entry.image?.formats;
  if (!formats && !entry.image?.url) return null;
  return STRAPI_URL + (formats?.large?.url || formats?.medium?.url || entry.image?.url);
}

// Wiederverwendbares Overlay fuer NOW-Beitraege mit Vor/Zurueck-Navigation
// (Pfeile + Touch-Swipe). Wird sowohl beim ersten Kartenbesuch automatisch
// gezeigt (Map.js) als auch per Klick auf den Event-Knopf in der NavigationBar.
//
// Props:
//   entries: Array der Beitraege, neueste zuerst (index 0 = neuester)
//   startIndex: mit welchem Beitrag geoeffnet wird (default 0)
//   onClose: Callback zum Schliessen
const NowOverlay = ({ entries, startIndex = 0, onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const touchStartX = useRef(null);

  const isOpen = Array.isArray(entries) && entries.length > 0;
  const entry = isOpen ? entries[Math.min(index, entries.length - 1)] : null;
  const canGoNewer = index > 0;
  const canGoOlder = isOpen && index < entries.length - 1;

  // Bei jedem Oeffnen (neue entries-Liste) auf den gewuenschten Startpunkt springen
  useEffect(() => {
    setIndex(startIndex);
  }, [entries, startIndex]);

  useEffect(() => {
    if (!entry) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && canGoOlder) setIndex(i => i + 1);
      if (e.key === 'ArrowLeft' && canGoNewer) setIndex(i => i - 1);
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [entry, onClose, canGoOlder, canGoNewer]);

  if (!entry) return null;

  const goOlder = () => canGoOlder && setIndex(i => i + 1);
  const goNewer = () => canGoNewer && setIndex(i => i - 1);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < 40) return;
    if (deltaX < 0) {
      // nach links gewischt -> naechster (aelterer) Beitrag
      goOlder();
    } else {
      // nach rechts gewischt -> zurueck zum neueren Beitrag
      goNewer();
    }
  };

  return (
    <div
      className="gw-detail-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="gw-detail-panel"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          className="gw-detail-close"
          onClick={onClose}
          aria-label="Schliessen"
        >
          ✕
        </button>
        <span className="gw-now-badge">NOW</span>
        {entry.caption && (
          <h2 className="gw-detail-headline">{entry.caption}</h2>
        )}
        {entry.image && (
          <img
            src={getNowImageUrl(entry)}
            alt={entry.caption || 'NOW'}
            className="gw-detail-img"
          />
        )}
        {entry.credit && (
          <p className="gw-detail-credit">{entry.credit}</p>
        )}

        {entries.length > 1 && (
          <div className="gw-now-nav">
            <button
              type="button"
              className="gw-now-nav-arrow"
              onClick={goNewer}
              disabled={!canGoNewer}
              aria-label="Neuerer Beitrag"
            >
              ← Neuer
            </button>
            <span className="gw-now-nav-count">{index + 1} / {entries.length}</span>
            <button
              type="button"
              className="gw-now-nav-arrow"
              onClick={goOlder}
              disabled={!canGoOlder}
              aria-label="Älterer Beitrag"
            >
              Älter →
            </button>
          </div>
        )}

        {entry.body && (
          <div className="gw-detail-body">
            {entry.body.split('\n').map((para, i) =>
              para.trim() ? <p key={i}>{para}</p> : null
            )}
          </div>
        )}
        {entry.link && (
          <div className="gw-detail-link-row">
            <a
              href={entry.link}
              target="_blank"
              rel="noopener noreferrer"
              className="gw-now-nav-arrow"
            >
              Mehr erfahren →
            </a>
          </div>
        )}
        <a href="/gallery-weekend" className="gw-now-all-link">
          Zum NOW-Archiv →
        </a>
      </div>
    </div>
  );
};

export default NowOverlay;
